import type {BallotResource, SaveDraftBody, VotingSystem} from '../../shared/ballotTypes';

export type EditorPerson = {humaansId: string; name: string; photoUrl: string | null};
export type EditorOption = {
    id: string;
    title: string;
    description: string;
    people: EditorPerson[];
    titleTouched: boolean;
};
export type EditorDraft = {
    title: string;
    description: string;
    system: VotingSystem;
    dotsPerVoter: number;
    durationSeconds: number;
    musicTrackId: string | null;
    blockSelfVotes: boolean;
    selfVotesTouched: boolean;
    options: EditorOption[];
};
type Field = Exclude<keyof EditorDraft, 'options' | 'selfVotesTouched'>;
export type ImportedOption = {id: string; description: string; people: EditorPerson[]};
type OptionPatch = Partial<Pick<EditorOption, 'title' | 'description'>>;

export type EditorAction =
    | {type: 'set'; field: Field; value: EditorDraft[Field]}
    | {type: 'addOption'; id: string}
    | {type: 'importOptions'; options: ImportedOption[]}
    | {type: 'addPerson'; optionId: string; person: EditorPerson}
    | {type: 'removePerson'; optionId: string; humaansId: string}
    | {type: 'updateOption'; id: string; patch: OptionPatch}
    | {type: 'removeOption'; id: string}
    | {type: 'moveOption'; from: number; to: number};

const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? '';

// Full names for one or two people; first names for three or more ("Sarah, Tom & Aisha").
export const peopleTitle = (names: string[]): string => {
    if (names.length <= 1) return names[0] ?? '';
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    const firsts = names.map(firstName);
    return `${firsts.slice(0, -1).join(', ')} & ${firsts.at(-1)}`;
};

// `directory` maps Humaans id to name; anyone it cannot name makes the title count as typed.
export const fromResource = (
    b: BallotResource,
    directory: ReadonlyMap<string, string> = new Map(),
): EditorDraft => ({
    title: b.title,
    description: b.description ?? '',
    system: b.system,
    dotsPerVoter: b.dots_per_voter,
    durationSeconds: b.duration_seconds,
    musicTrackId: b.music_track_id,
    blockSelfVotes: b.block_self_votes,
    selfVotesTouched: b.options.length > 0,
    options: b.options.map((o) => {
        const known = o.humaans_ids.every((id) => directory.has(id));
        const people = o.humaans_ids.map((humaansId, i) => ({
            humaansId,
            name: directory.get(humaansId) ?? 'Unknown person',
            photoUrl: o.photo_urls[i] ?? null,
        }));
        return {
            id: o.id,
            title: o.title,
            description: o.description ?? '',
            people,
            titleTouched:
                people.length === 0 || !known || o.title !== peopleTitle(people.map((p) => p.name)),
        };
    }),
});

const blank = (id: string): EditorOption => ({
    id,
    title: '',
    description: '',
    people: [],
    titleTouched: false,
});

// The option title limit the API enforces; an auto-filled title is cut to fit so it always saves.
const TITLE_LIMIT = 120;

const withPeople = (option: EditorOption, people: EditorPerson[]): EditorOption => ({
    ...option,
    people,
    title: option.titleTouched
        ? option.title
        : peopleTitle(people.map((p) => p.name)).slice(0, TITLE_LIMIT),
});

const mapOption = (
    draft: EditorDraft,
    id: string,
    change: (o: EditorOption) => EditorOption,
): EditorDraft => ({
    ...draft,
    options: draft.options.map((o) => (o.id === id ? change(o) : o)),
});

export const editorReducer = (draft: EditorDraft, action: EditorAction): EditorDraft => {
    switch (action.type) {
        case 'set':
            return {
                ...draft,
                [action.field]: action.value,
                ...(action.field === 'blockSelfVotes' ? {selfVotesTouched: true} : {}),
            };
        case 'addOption':
            return {...draft, options: [...draft.options, blank(action.id)]};
        case 'importOptions': {
            const added = action.options.map((o) =>
                withPeople({...blank(o.id), description: o.description}, o.people),
            );
            const withPeopleAdded = action.options.some((o) => o.people.length > 0);
            return {
                ...draft,
                options: [...draft.options, ...added],
                blockSelfVotes:
                    withPeopleAdded && !draft.selfVotesTouched ? true : draft.blockSelfVotes,
            };
        }
        case 'addPerson': {
            const {person} = action;
            const next = mapOption(draft, action.optionId, (o) =>
                o.people.some((p) => p.humaansId === person.humaansId)
                    ? o
                    : withPeople(o, [...o.people, person]),
            );
            return {
                ...next,
                blockSelfVotes: draft.selfVotesTouched ? draft.blockSelfVotes : true,
            };
        }
        case 'removePerson':
            return mapOption(draft, action.optionId, (o) =>
                withPeople(
                    o,
                    o.people.filter((p) => p.humaansId !== action.humaansId),
                ),
            );
        case 'updateOption':
            return mapOption(draft, action.id, (o) => ({
                ...o,
                ...action.patch,
                // Clearing the title hands it back to the people auto-fill.
                ...(action.patch.title !== undefined
                    ? {titleTouched: action.patch.title !== ''}
                    : {}),
            }));
        case 'removeOption':
            return {...draft, options: draft.options.filter((o) => o.id !== action.id)};
        case 'moveOption': {
            const options = [...draft.options];
            const [moved] = options.splice(action.from, 1);
            options.splice(action.to, 0, moved);
            return {...draft, options};
        }
    }
};

export const toSaveBody = (d: EditorDraft): SaveDraftBody => ({
    title: d.title.trim() || 'Untitled vote',
    description: d.description.trim() || null,
    system: d.system,
    dots_per_voter: d.dotsPerVoter,
    duration_seconds: d.durationSeconds,
    music_track_id: d.musicTrackId,
    block_self_votes: d.blockSelfVotes,
    options: d.options
        .filter((o) => o.title.trim().length > 0)
        .map((o) => ({
            id: o.id,
            title: o.title.trim(),
            description: o.description.trim() || null,
            humaans_ids: o.people.map((p) => p.humaansId),
        })),
});
